/**
 * Horário de atendimento e limites próprios de uma agenda (resource.Schedule).
 * Espelha public/api/resources/ResourceSchedule.php: null = segue o horário da empresa.
 *
 * Formato:
 *   { hours: { "1": [["08:00","12:00"]] } | null, slotMinutes, maxPerDay, maxPerMorning, maxPerAfternoon }
 * Dias da semana: 0 = domingo .. 6 = sábado.
 */

export const WEEKDAYS = [
    { day: 1, short: 'Seg', label: 'Segunda' },
    { day: 2, short: 'Ter', label: 'Terça' },
    { day: 3, short: 'Qua', label: 'Quarta' },
    { day: 4, short: 'Qui', label: 'Quinta' },
    { day: 5, short: 'Sex', label: 'Sexta' },
    { day: 6, short: 'Sáb', label: 'Sábado' },
    { day: 0, short: 'Dom', label: 'Domingo' },
];

export const SLOT_MINUTES_OPTIONS = [5, 10, 15, 20, 30, 45, 60];
export const MAX_INTERVALS_PER_DAY = 4;
export const AFTERNOON_START_MINUTES = 12 * 60;
export const LIMIT_KEYS = ['maxPerDay', 'maxPerMorning', 'maxPerAfternoon'];

export const toMinutes = (time) => {
    const [h, m] = String(time || '0:0').split(':');
    return (parseInt(h, 10) || 0) * 60 + (parseInt(m, 10) || 0);
};

export const minutesToTime = (minutes) => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

/**
 * Horário da empresa no formato de faixas por dia: base para quem passa a ter
 * horário próprio (já vem com o almoço bloqueado da empresa fora das faixas).
 */
export const companyHoursAsIntervals = (companyConfig) => {
    const days = companyConfig?.AvailableDaysOfWeek || [1, 2, 3, 4, 5];
    const [startHour, endHour] = companyConfig?.AvailableTime || [8, 18];
    const step = Number(companyConfig?.TimeInterval) || 0.5;
    const blocked = (companyConfig?.BlockedTimes || []).map(Number);

    const intervals = [];
    let current = null;
    for (let hour = Number(startHour); hour < Number(endHour) - 1e-9; hour += step) {
        const slotStart = Math.round(hour * 60);
        const slotEnd = Math.min(Math.round((hour + step) * 60), Math.round(Number(endHour) * 60));
        const isBlocked = blocked.some((b) => Math.abs(b - hour) < 1e-6);
        if (isBlocked) {
            current = null;
        } else if (current && current[1] === slotStart) {
            current[1] = slotEnd;
        } else {
            current = [slotStart, slotEnd];
            intervals.push(current);
        }
    }
    const asTimes = intervals.slice(0, MAX_INTERVALS_PER_DAY).map(([s, e]) => [minutesToTime(s), minutesToTime(e)]);

    const hours = {};
    days.forEach((day) => {
        hours[String(day)] = asTimes.map((pair) => [...pair]);
    });
    return hours;
};

/** Faixas do dia da semana em minutos, ou null quando a agenda segue a empresa. */
export const intervalsForWeekday = (schedule, weekday) => {
    if (!schedule?.hours) return null;
    return (schedule.hours[String(weekday)] || []).map(([s, e]) => [toMinutes(s), toMinutes(e)]);
};

/** Trechos da coluna [startHour, endHour) fora das faixas do profissional, em minutos. */
export const offHoursRanges = (intervals, startHour, endHour) => {
    const ranges = [];
    let cursor = startHour * 60;
    const end = endHour * 60;
    [...intervals].sort((a, b) => a[0] - b[0]).forEach(([from, to]) => {
        if (from > cursor) ranges.push([cursor, Math.min(from, end)]);
        cursor = Math.max(cursor, to);
    });
    if (cursor < end) ranges.push([cursor, end]);
    return ranges.filter(([from, to]) => to > from);
};

/**
 * Faixa de horas da grade da agenda: do início mais cedo ao término mais tarde
 * entre o horário da empresa e o horário próprio de cada agenda.
 * { startHour, endHour, widened } (widened = alguma agenda passa do horário da empresa).
 */
export const calendarHourRange = (companyConfig, fallback = [8, 20]) => {
    const companyStart = Number(companyConfig?.AvailableTime?.[0]) || fallback[0];
    const companyEnd = Number(companyConfig?.AvailableTime?.[1]) || fallback[1];
    let startHour = companyStart;
    let endHour = companyEnd;
    (companyConfig?.Resources || []).forEach((resource) => {
        Object.values(resource?.Schedule?.hours || {}).forEach((intervals) => {
            (intervals || []).forEach(([from, to]) => {
                startHour = Math.min(startHour, Math.floor(toMinutes(from) / 60));
                endHour = Math.max(endHour, Math.ceil(toMinutes(to) / 60));
            });
        });
    });
    return {
        startHour,
        endHour: Math.min(24, endHour),
        widened: startHour < companyStart || endHour > companyEnd,
        companyHours: [companyStart, companyEnd],
    };
};

/**
 * O horário cai dentro do expediente da agenda?
 * true/false quando a agenda tem horário próprio; null quando segue a empresa.
 * Com duração, o atendimento inteiro precisa caber numa faixa.
 */
export const isWithinResourceHours = (schedule, date, time, durationMinutes = 0) => {
    if (!schedule?.hours || !date || !time) return null;
    // "YYYY-MM-DD" sozinho vira UTC no new Date(): monta a data local
    const day = typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)
        ? new Date(`${date}T00:00:00`)
        : new Date(date);
    const intervals = intervalsForWeekday(schedule, day.getDay());
    const start = toMinutes(time);
    const end = start + Math.max(0, Number(durationMinutes) || 0);
    return intervals.some(([s, e]) => start >= s && start < e && end <= e);
};

/** Resumo curto para a lista de agendas: "Seg, Qui · 13:00–18:00". */
export const describeResourceHours = (schedule) => {
    if (!schedule?.hours) return '';
    const groups = [];
    WEEKDAYS.forEach(({ day, short }) => {
        const intervals = schedule.hours[String(day)];
        if (!intervals || intervals.length === 0) return;
        const text = intervals.map(([s, e]) => `${s}–${e}`).join(', ');
        const group = groups.find((g) => g.text === text);
        if (group) group.days.push(short);
        else groups.push({ text, days: [short] });
    });
    return groups.map((g) => `${g.days.join(', ')} · ${g.text}`).join(' | ');
};

/**
 * Resumo do que a agenda tem de próprio, para o selo da lista:
 * { label, lines } ou null quando segue a empresa em tudo.
 */
export const describeResourceSchedule = (schedule) => {
    if (!schedule) return null;
    const lines = [];
    const hoursText = describeResourceHours(schedule);
    if (hoursText) lines.push(...hoursText.split(' | '));
    if (schedule.slotMinutes) lines.push(`Agendamento online a cada ${schedule.slotMinutes} min`);
    const limits = [
        schedule.maxPerDay && `${schedule.maxPerDay} por dia`,
        schedule.maxPerMorning && `${schedule.maxPerMorning} de manhã`,
        schedule.maxPerAfternoon && `${schedule.maxPerAfternoon} à tarde`,
    ].filter(Boolean);
    if (limits.length > 0) lines.push(`Máximo: ${limits.join(', ')}`);
    if (lines.length === 0) return null;
    // Um selo só, seja horário, intervalo ou limite: o detalhe fica no tooltip
    return { label: 'Regras próprias', lines: ['No lugar de Preferências:', ...lines] };
};

/** Remove chaves vazias; devolve null quando nada é próprio (segue a empresa). */
export const cleanResourceSchedule = (schedule) => {
    if (!schedule) return null;
    const out = {
        hours: schedule.hours || null,
        slotMinutes: schedule.slotMinutes ? Number(schedule.slotMinutes) : null,
    };
    LIMIT_KEYS.forEach((key) => {
        const value = parseInt(schedule[key], 10);
        out[key] = value > 0 ? value : null;
    });
    return Object.values(out).some((v) => v !== null) ? out : null;
};
